import { createContext, useContext, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageContextType {
  language: string;
  isRTL: boolean;
  changeLanguage: (lng: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  
  const isRTL = i18n.language === 'he';

  useEffect(() => {
    // Set language and text direction class
    // Only set language, not direction
    document.documentElement.lang = i18n.language;
    // Add CSS class for text direction without affecting layout
    if (isRTL) {
      document.documentElement.classList.add('text-rtl');
    } else {
      document.documentElement.classList.remove('text-rtl');
    }
  }, [isRTL, i18n.language]);

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
  };

  const value: LanguageContextType = {
    language: i18n.language,
    isRTL,
    changeLanguage,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}